import React, { useEffect, useState, useMemo } from 'react';
import { useAgentStream } from '../../../lib/agentloop-stream';
import type { ToolComponent } from '../types';
import { MessageStream } from './MessageStream';
import { MessageInput } from './MessageInput';
import { AgentStatusBar } from './AgentStatusBar';
import type { Model } from '../controls/ModelSelector';

interface AgentChatProps {
  // Stream configuration
  streamUrl: string;
  streamMethod?: 'GET' | 'POST';
  streamHeaders?: Record<string, string>;
  autoConnect?: boolean;

  // Message sending
  onSendMessage: (message: string) => Promise<void>;
  onInterrupt?: () => Promise<void>;
  onTryAgain?: () => Promise<void>;

  // UI customization
  className?: string;
  placeholder?: string;
  showInterruptButton?: boolean;

  // Model selection (optional)
  showModelSelector?: boolean;
  models?: Model[];
  selectedModel?: string;
  onModelChange?: (model: string) => void;

  // Tool registry
  toolRegistry?: Map<string, ToolComponent>;

  // Props for assistant messages (secret handling)
  applicationId?: string;

  // Working state text (supports string or React component like VibeText)
  workingText?: string | React.ReactNode | ((isRunning: boolean, isInterrupting: boolean) => React.ReactNode);

  // Legacy - custom status text (deprecated, use workingText instead)
  customStatusText?: string | ((isRunning: boolean, isInterrupting: boolean) => string);

  // ESC key handling
  enableEscapeToInterrupt?: boolean;
}

export const AgentChat: React.FC<AgentChatProps> = ({
  streamUrl,
  streamMethod = 'POST',
  streamHeaders = {},
  autoConnect = true,
  onSendMessage,
  onInterrupt,
  onTryAgain,
  className = '',
  placeholder = 'Type a message...',
  showInterruptButton = true,
  showModelSelector = false,
  models = [],
  selectedModel = '',
  onModelChange,
  toolRegistry,
  applicationId,
  workingText,
  customStatusText,
  enableEscapeToInterrupt = true,
}) => {
  const [isInterrupting, setIsInterrupting] = useState(false);

  // Use agentloop-stream hook
  const { blocks, isAgentRunning, error } = useAgentStream({
    url: streamUrl,
    method: streamMethod,
    headers: streamHeaders,
    autoConnect,
  });

  // Latest error state
  const latestError = useMemo(() => {
    if (!error) return undefined;
    return {
      id: `error-${Date.now()}`,
      content: error,
      errorType: undefined,
    };
  }, [error]);

  // Handle ESC key for interrupt
  useEffect(() => {
    if (!enableEscapeToInterrupt) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isAgentRunning && !isInterrupting) {
        event.preventDefault();
        handleInterrupt();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAgentRunning, isInterrupting, enableEscapeToInterrupt]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInterrupt = async () => {
    if (!onInterrupt || !isAgentRunning || isInterrupting) return;

    setIsInterrupting(true);
    try {
      await onInterrupt();
    } catch (error) {
      console.error('Failed to interrupt:', error);
    } finally {
      setTimeout(() => {
        setIsInterrupting(false);
      }, 500);
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!message.trim() || isAgentRunning) return;

    try {
      await onSendMessage(message);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  return (
    <div className={`h-full flex flex-col ${className}`}>
      {/* Message Stream */}
      <div className="flex-1 overflow-hidden">
        <MessageStream
          blocks={blocks}
          latestError={latestError}
          onTryAgain={onTryAgain}
          toolRegistry={toolRegistry}
          applicationId={applicationId}
          onSendMessage={async (msg, model) => {
            // For secret continuation messages
            await handleSendMessage(msg);
          }}
          className="h-full p-4"
        />
      </div>

      {/* Status Bar */}
      {isAgentRunning && (
        <AgentStatusBar
          isRunning={isAgentRunning}
          isInterrupting={isInterrupting}
          workingText={workingText}
          statusText={customStatusText}
          showInterruptHint={showInterruptButton}
        />
      )}

      {/* Message Input */}
      <div className="border-t p-2 flex-shrink-0">
        <MessageInput
          onSendMessage={handleSendMessage}
          disabled={isAgentRunning}
          placeholder={
            isAgentRunning
              ? 'Agent is running...'
              : placeholder
          }
          showModelSelector={showModelSelector}
          models={models}
          selectedModel={selectedModel}
          onModelChange={onModelChange}
        />
      </div>
    </div>
  );
};
